<template>
  <v-form ref="form" v-model="valid">
    <v-card class="pa-3">
      <v-card-text>
        <v-select
          v-if="userIsNationalAdmin"
          v-model="formData.type"
          :items="types"
          :label="$t('common.Type')"
          required
          :rules="mandatoryRule"
        />

        <v-select
          v-if="userIsNationalAdmin"
          :rules="mandatoryRule"
          v-model="formData.where"
          :items="whereItems"
          :label="$t('common.Where')"
        />

        <v-text-field
          v-if="!formData.id"
          v-model="formData.title"
          :label="$t('common.Title')"
          required
          :rules="mandatoryRule"
        />
        <div v-if="!formData.id">
          <v-menu
            v-model="datePicker"
            :close-on-content-click="false"
            :nudge-right="40"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="formData.date"
                :label="$t('common.Date')"
                prepend-icon="event"
                readonly
                :rules="mandatoryRule"
                required
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="formData.date" @input="datePicker = false"></v-date-picker>
          </v-menu>
        </div>

        <v-layout>
          <v-flex class="mr-2" xs6>
            <v-menu
              ref="fromHourPicker"
              v-model="fromHourPicker"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="formData.fromHour"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="formData.fromHour"
                  :label="$t('common.FromHour')"
                  prepend-icon="access_time"
                  :rules="mandatoryRule"
                  required
                  readonly
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="fromHourPicker"
                v-model="formData.fromHour"
                full-width
                format="24hr"
                @click:minute="$refs.fromHourPicker.save(formData.fromHour)"
              ></v-time-picker>
            </v-menu>
          </v-flex>
          <v-flex class="ml-2" xs6>
            <v-menu
              ref="toHourPicker"
              v-model="toHourPicker"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="formData.toHour"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="formData.toHour"
                  :label="$t('common.ToHour')"
                  prepend-icon="access_time"
                  :rules="mandatoryRule"
                  required
                  readonly
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="toHourPicker"
                v-model="formData.toHour"
                full-width
                format="24hr"
                @click:minute="$refs.toHourPicker.save(formData.toHour)"
              ></v-time-picker>
            </v-menu>
          </v-flex>
        </v-layout>

        <v-text-field v-model="formData.local" :label="$t('common.Local')" :rules="mandatoryRule" />
        <v-text-field
          v-model="formData.organizer"
          :label="$t('common.Organizer')"
          :rules="mandatoryRule"
        />
        <v-text-field v-model="formData.link" :label="$t('common.Link')" :rules="urlRule" />

        <v-menu
          v-model="deadlinePicker"
          :close-on-content-click="false"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="formData.deadlineDate"
              :label="$t('calendar.RegistrationDeadline')"
              prepend-icon="event"
              readonly
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="formData.deadlineDate" @input="deadlinePicker = false"></v-date-picker>
        </v-menu>
      </v-card-text>

      <v-textarea
        v-model="formData.resume"
        :label="$t('common.Resume')"
        required
        :rules="mandatoryRule"
      />

      <v-card-actions>
        <v-btn
          :loading="loading"
          color="success"
          @click="submit"
          :disabled="!valid"
        >{{$t('common.Submit')}}</v-btn>
        <v-btn flat color="gray" @click="closeMe">{{$t('common.Cancel')}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import cms from "@/content";
export default {
  props: {
    closeMe: {
      required: true,
      type: Function
    },
    formData: {
      type: Object
    }
  },
  data() {
    return {
      datePicker: false,
      deadlinePicker: false,
      valid: false,
      fromHourPicker: false,
      toHourPicker: false,
      loading: false
    };
  },
  computed: {
    ...mapGetters(["user"]),
    userIsNationalAdmin() {
      return this.user && (this.user.roles || []).includes("national-admin");
    },
    mandatoryRule() {
      return [v => !!v || this.$t("common.RequiredField")];
    },
    types() {
      return cms.getCourseTypes(this.$i18n.locale);
    },
    whereItems() {
      return cms.getScopesPresentationType(this.$i18n.locale);
    },
    urlRule() {
      return [
        v =>
          !v ||
          /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(
            v
          ) ||
          this.$t("calendar.InvalidURL")
      ];
    }
  },
  methods: {
    ...mapActions(["addEvent", "updateEvent", "showSuccessSnackbar"]),
    submit() {
      this.loading = true;
      this.formData.open = false;

      if (!this.formData.id) {
        if (!this.userIsNationalAdmin) {
          this.formData.userId = this.$store.getters.profile.id;
        }

        this.formData.isFavorite = false;
      }

      if (this.userIsNationalAdmin) {
        this.formData.nationalAdminEventOf = this.user.specialty;
      }

      let action =
        this.formData && this.formData.id
          ? this.updateEvent({ id: this.formData.id, ...this.formData })
          : this.addEvent({ ...this.formData });

      action.then(() => {
        this.closeMe();

        if (this.formData && this.formData.id) {
          this.showSuccessSnackbar(this.$t("calendar.EventUpdated"));
        } else {
          this.showSuccessSnackbar(this.$t("calendar.EventCreated"));
        }

        this.$refs.form.reset();
        this.loading = false;
      });
    }
  }
};
</script>

<style>
</style>
